import React, { useState } from 'react';
import PageLayout from '@/components/layout/PageLayout';
import PageHero from '@/components/ui/PageHero';
import ScrollReveal from '@/components/ui/ScrollReveal';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';

const Portfolio = () => {
  const categories = [
    { id: 'all', name: 'All Work' },
    { id: 'websites', name: 'Websites' },
    { id: 'branding', name: 'Branding' },
    { id: 'photography', name: 'Photography' },
    { id: 'video', name: 'Video' },
    { id: 'marketing', name: 'Marketing' }
  ];

  const projects = [
    {
      id: 1,
      title: 'The Vladimir Collection',
      category: ['websites', 'branding'],
      image: 'https://app.milanote.com/media/p/images/1TZl4J1uaXLgcv/VFq/combined_screenshots_with_drawing%20%288%29.jpg',
      description: 'Luxury real estate website design',
      client: 'Vladimir Properties'
    },
    {
      id: 2,
      title: 'Pinnacle Roofing',
      category: ['websites', 'branding', 'logo'],
      image: 'https://app.milanote.com/media/p/images/1TZl4J1uaXLgcw/HWE/combined_screenshots_with_drawing%20%289%29.jpg',
      description: 'Full brand identity and website redesign',
      client: 'Pinnacle Roofing & Construction'
    },
    {
      id: 3,
      title: 'H.A. May Construction',
      category: ['websites', 'photography'],
      image: 'https://app.milanote.com/media/p/images/1TZlHu1uaXLgcx/1pk/combined_screenshots_with_drawing%20%2810%29.jpg',
      description: 'Construction company brand refresh and website',
      client: 'H.A. May Construction'
    },
    {
      id: 4,
      title: 'Cagley Homes',
      category: ['websites', 'photography'],
      image: 'https://app.milanote.com/media/p/images/1TZlJA1uaXLgcy/OP8/combined_screenshots_with_drawing%20%2811%29.jpg',
      description: 'Website Rebuild, Website Hosting and Management, Photography',
      client: 'Cagley Homes'
    },
    {
      id: 5,
      title: 'Luxury Property Photography',
      category: ['photography', 'marketing'],
      image: 'https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/luxury-property-photography.jpg',
      description: 'Professional real estate photography',
      client: 'Various Real Estate Clients'
    },
    {
      id: 6,
      title: 'New Construction Photography',
      category: ['photography'],
      image: 'https://media.milanote.com/p/images/1P2Vke1lxhWe3R/QgY/Cagley+Family+Home+-+Rouzan+%282+of+35%29.jpg',
      description: 'Premium photography for new construction properties',
      client: 'Cagley Homes'
    },
    {
      id: 7,
      title: 'Virtual Twilight Real Estate',
      category: ['photography', 'marketing'],
      image: 'https://media.milanote.com/p/images/1OXGwL1PCHmxbQ/7Bp/Antonio+Lopez+-+11012+Crossover+Rd+%2823+of+24%29.jpg',
      description: 'Virtual twilight photography for real estate listings',
      client: 'Luxury Properties'
    },
    {
      id: 8,
      title: 'LED Business Cards',
      category: ['branding', 'marketing'],
      image: 'https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/Hand_holding_a_business_card_1.jpg',
      description: 'Illuminated LED business cards for standout branding',
      client: 'Various Clients'
    },
    {
      id: 9,
      title: 'Restaurant LED Menu',
      category: ['branding', 'marketing'],
      image: 'https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/LED_light_menu_mockup_yesthisoneisai_but_we_really_make_these.png',
      description: 'Custom LED-enhanced menu design for restaurants',
      client: 'Hospitality Clients'
    },
    {
      id: 10,
      title: 'Social Media Scheduler',
      category: ['marketing', 'websites'],
      image: 'https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/social_scheduler.jpg',
      description: 'Social media management tool implementation',
      client: 'Various Business Clients'
    },
    {
      id: 11,
      title: 'Tech Coaching Sessions',
      category: ['marketing'],
      image: 'https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/Person_working_with_laptop_notes.jpg',
      description: 'Personalized technology coaching and implementation',
      client: 'Entrepreneurs and Businesses'
    },
    {
      id: 12,
      title: '360° Virtual Property Tour',
      category: ['photography', 'video'],
      image: 'https://images.unsplash.com/photo-1600607686527-6fb886090705?q=80&w=1000&auto=format&fit=crop',
      description: 'Immersive 360° virtual tours for real estate',
      client: 'Real Estate Agencies'
    }
  ];

  const testimonials = [
    {
      name: "Simon Shuler",
      title: "Luxury Real Estate Agent",
      quote: "Evan is very patient, and delivers exceptional quality service and media. I was thoroughly impressed with my listing photos, cinematic property tour, landing pages, and 3D walkthrough… Highly recommend."
    },
    {
      name: "Nick Cagley",
      title: "Cagley Homes - Luxury Homes",
      quote: "Evan has been photographing and filming my projects the last few years. He is very professional and does a very good job of getting all the details we want for our projects. I look forward to photographing our next project with him!"
    },
    {
      name: "Kaylee Brock",
      title: "Marketing Manager",
      quote: "Evan helped us shoot a lifestyle video for an apartment complex in Baton Rouge. He was very accommodating with any changes we needed to make and very responsive. Overall, we had a great experience, and the video turned out great!"
    }
  ];

  const pageTitle = "Portfolio & Case Studies | Baton Rouge, LA | Astrem";
  const pageDescription = "Explore our portfolio of websites, branding, photography and marketing projects in Baton Rouge, LA. Astrem delivers exceptional results for businesses across industries. View our work and get inspired today!";

  return (
    <PageLayout 
      title={pageTitle}
      description={pageDescription}
      ogImage="https://astrem.co/lovable-uploads/a1afc3e0-0836-4e40-9b00-14ef27c65e43.png"
      customCta="View our work and get inspired today!"
    >
      <PageHero 
        title="Our Portfolio" 
        subtitle="Showcasing Our Best Work"
        description="Browse through our collection of projects spanning web design, branding, photography, and digital marketing. See how we help businesses and individuals bring their visions to life."
        ctaText="Get in Touch"
        ctaLink="/contact"
        backgroundImage="https://images.unsplash.com/photo-1542744095-fcf48d80b0fd?q=80&w=1000&auto=format&fit=crop"
      />

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                Testimonials
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">What Our Clients Say</h2>
              <p className="text-lg text-astrem-gray-600">
                Don't just take our word for it. Here's what our clients have to say about working with us.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <div className="bg-white p-6 rounded-lg border border-astrem-gray-200 shadow-sm h-full flex flex-col">
                  <p className="text-astrem-gray-600 italic mb-6 flex-grow">"{testimonial.quote}"</p>
                  <div>
                    <h4 className="font-medium">{testimonial.name}</h4>
                    <p className="text-sm text-astrem-gray-600">{testimonial.title}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Tab Section */}
      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center max-w-3xl mx-auto mb-12">
              <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                Our Work
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">Featured Projects</h2>
              <p className="text-lg text-astrem-gray-600">
                Browse through our projects by category or view all of our recent work.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={100}>
            <Tabs defaultValue="all" className="w-full">
              <TabsList className="flex flex-wrap justify-center mb-8 bg-transparent h-auto p-1">
                {categories.map((category) => (
                  <TabsTrigger 
                    key={category.id} 
                    value={category.id}
                    className="data-[state=active]:bg-astrem-black data-[state=active]:text-white rounded-full px-4 py-2 m-1"
                  >
                    {category.name}
                  </TabsTrigger>
                ))}
              </TabsList>
              
              {categories.map((category) => (
                <TabsContent key={category.id} value={category.id} className="mt-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {projects
                      .filter(project => category.id === 'all' || project.category.includes(category.id))
                      .map((project) => (
                        <Card key={project.id} className="overflow-hidden border-0 shadow-md hover:shadow-lg transition-all duration-300">
                          <div className="aspect-video relative overflow-hidden">
                            <img 
                              src={project.image} 
                              alt={project.title} 
                              className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                            />
                          </div>
                          <CardContent className="p-6">
                            <h3 className="text-xl font-medium mb-1">{project.title}</h3>
                            <p className="text-sm text-astrem-gray-500 mb-3">Client: {project.client}</p>
                            <p className="text-astrem-gray-600">{project.description}</p>
                          </CardContent>
                        </Card>
                    ))}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </ScrollReveal>
        </div>
      </section>
      
      {/* Case Studies Highlight Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                Case Studies
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">Project Showcases</h2>
              <p className="text-lg text-astrem-gray-600">
                Take a deeper look at some of our most impactful projects and the results we achieved.
              </p>
            </div>
          </ScrollReveal>

          <div className="space-y-16">
            <ScrollReveal>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <img 
                    src="https://app.milanote.com/media/p/images/1TZl4J1uaXLgcv/VFq/combined_screenshots_with_drawing%20%288%29.jpg" 
                    alt="The Vladimir Collection" 
                    className="rounded-lg shadow-xl"
                  />
                </div>
                <div>
                  <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                    Website & Branding
                  </span>
                  <h3 className="text-2xl md:text-3xl font-display font-medium mb-4">The Vladimir Collection</h3>
                  <p className="text-lg text-astrem-gray-600 mb-6">
                    We created a luxury real estate website with sophisticated branding to showcase high-end properties. The elegant design reflects the premium nature of the properties while providing an intuitive user experience.
                  </p>
                  <div className="space-y-4 mb-8">
                    <div className="flex items-start">
                      <div className="w-10 h-10 flex-shrink-0 bg-astrem-gray-100 rounded-full flex items-center justify-center mr-4">
                        <span className="font-medium">1</span>
                      </div>
                      <div>
                        <h4 className="font-medium mb-1">Challenge</h4>
                        <p className="text-astrem-gray-600">Creating a digital presence that matched the luxury real estate brand's upscale positioning.</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-10 h-10 flex-shrink-0 bg-astrem-gray-100 rounded-full flex items-center justify-center mr-4">
                        <span className="font-medium">2</span>
                      </div>
                      <div>
                        <h4 className="font-medium mb-1">Solution</h4>
                        <p className="text-astrem-gray-600">Premium website design with sophisticated branding elements and optimized property showcases.</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-10 h-10 flex-shrink-0 bg-astrem-gray-100 rounded-full flex items-center justify-center mr-4">
                        <span className="font-medium">3</span>
                      </div>
                      <div>
                        <h4 className="font-medium mb-1">Results</h4>
                        <p className="text-astrem-gray-600">Increased engagement, higher-quality leads, and elevated brand perception in the luxury market.</p>
                      </div>
                    </div>
                  </div>
                  <Button asChild variant="outline" className="rounded-full">
                    <Link to="/contact">
                      Discuss Your Project
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={100}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div className="order-2 lg:order-1">
                  <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                    Photography & Marketing
                  </span>
                  <h3 className="text-2xl md:text-3xl font-display font-medium mb-4">Luxury Real Estate Photography</h3>
                  <p className="text-lg text-astrem-gray-600 mb-6">
                    Our professional photography services help real estate properties stand out in a competitive market. We capture the essence and unique features of each property with exceptional attention to detail.
                  </p>
                  <div className="space-y-4 mb-8">
                    <div className="flex items-start">
                      <div className="w-10 h-10 flex-shrink-0 bg-astrem-gray-100 rounded-full flex items-center justify-center mr-4">
                        <span className="font-medium">1</span>
                      </div>
                      <div>
                        <h4 className="font-medium mb-1">Challenge</h4>
                        <p className="text-astrem-gray-600">Creating compelling visual content that showcases properties in their best light.</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-10 h-10 flex-shrink-0 bg-astrem-gray-100 rounded-full flex items-center justify-center mr-4">
                        <span className="font-medium">2</span>
                      </div>
                      <div>
                        <h4 className="font-medium mb-1">Solution</h4>
                        <p className="text-astrem-gray-600">Professional photography with advanced techniques, optimal lighting, and strategic compositions.</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-10 h-10 flex-shrink-0 bg-astrem-gray-100 rounded-full flex items-center justify-center mr-4">
                        <span className="font-medium">3</span>
                      </div>
                      <div>
                        <h4 className="font-medium mb-1">Results</h4>
                        <p className="text-astrem-gray-600">Higher engagement on listings, reduced time on market, and increased sale prices.</p>
                      </div>
                    </div>
                  </div>
                  <Button asChild variant="outline" className="rounded-full">
                    <Link to="/services/real-estate-media">
                      Learn More
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
                <div className="order-1 lg:order-2">
                  <img 
                    src="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/luxury-property-photography.jpg" 
                    alt="Luxury Real Estate Photography" 
                    className="rounded-lg shadow-xl"
                  />
                </div>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <img 
                    src="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/LED_light_menu_mockup_yesthisoneisai_but_we_really_make_these.png" 
                    alt="LED Enhanced Menu" 
                    className="rounded-lg shadow-xl"
                  />
                </div>
                <div>
                  <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                    Innovative Technology
                  </span>
                  <h3 className="text-2xl md:text-3xl font-display font-medium mb-4">LED-Enhanced Restaurant Menus</h3>
                  <p className="text-lg text-astrem-gray-600 mb-6">
                    Our revolutionary LED printing technology creates illuminated menus that capture attention and enhance the dining experience. These innovative menus become conversation pieces that set restaurants apart.
                  </p>
                  <div className="space-y-4 mb-8">
                    <div className="flex items-start">
                      <div className="w-10 h-10 flex-shrink-0 bg-astrem-gray-100 rounded-full flex items-center justify-center mr-4">
                        <span className="font-medium">1</span>
                      </div>
                      <div>
                        <h4 className="font-medium mb-1">Challenge</h4>
                        <p className="text-astrem-gray-600">Creating memorable menu experiences in a competitive hospitality market.</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-10 h-10 flex-shrink-0 bg-astrem-gray-100 rounded-full flex items-center justify-center mr-4">
                        <span className="font-medium">2</span>
                      </div>
                      <div>
                        <h4 className="font-medium mb-1">Solution</h4>
                        <p className="text-astrem-gray-600">Ultra-thin LED technology embedded into custom menu designs.</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-10 h-10 flex-shrink-0 bg-astrem-gray-100 rounded-full flex items-center justify-center mr-4">
                        <span className="font-medium">3</span>
                      </div>
                      <div>
                        <h4 className="font-medium mb-1">Results</h4>
                        <p className="text-astrem-gray-600">Enhanced customer experience, increased social media shares, and strengthened brand differentiation.</p>
                      </div>
                    </div>
                  </div>
                  <Button asChild variant="outline" className="rounded-full">
                    <Link to="/services/led-printing">
                      Learn More
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-astrem-black text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <ScrollReveal>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                Ready to Start Your Project?
              </h2>
            </ScrollReveal>
            
            <ScrollReveal delay={100}>
              <p className="text-lg text-astrem-gray-300 mb-8">
                Contact us today to discuss how we can help bring your vision to life with our expertise in web design, photography, marketing, and innovative technology solutions.
              </p>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <Button asChild size="lg" className="bg-white text-astrem-black hover:bg-astrem-gray-100 rounded-full px-8">
                <Link to="/contact">Get in Touch</Link>
              </Button>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default Portfolio;
